import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Apple, Play, Calculator, Package, CloudSun, Flame, Globe2, Sparkles } from "lucide-react";
import { GRASS_BG } from "@/lib/landing";
import LiveClimate from "@/components/landing/LiveClimate";

const LINES = ["Precision care for", "the lawn you've", "always wanted."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } },
};
const lineMask = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden pt-28">
      {/* Parallax grass background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0 bg-cover bg-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-bottom"
          style={{ backgroundImage: `url(${GRASS_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--ld-bg)] via-[var(--ld-bg)]/70 to-[var(--ld-bg)]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ld-bg)] via-[var(--ld-bg)]/40 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto grid max-w-7xl items-start gap-12 px-5 pb-24 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:pb-32">
        {/* Left copy */}
        <motion.div style={{ opacity: fade }}>
          <motion.h1 variants={container} initial="hidden" animate="show" className="ld-display text-[clamp(2.75rem,7vw,5.5rem)]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  variants={lineMask}
                  className={`block ${i === 2 ? "text-[var(--ld-lime)]" : "text-[var(--ld-text)]"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-6 max-w-md text-base leading-relaxed text-[var(--ld-muted)] sm:text-lg"
          >
            Precise NPK math, live weather, GDD tracking, and AI photo
            diagnosis — all tuned to your climate, soil, and grass type.
            Everything a healthy lawn needs, in one app.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
            id="get"
          >
            <a href="https://apps.apple.com/app/id6786804897" target="_blank" rel="noopener noreferrer" data-testid="hero-appstore" className="ld-btn-lime inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-black">
              <Apple className="h-5 w-5" /> Download on the App Store
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.growcalc.app" target="_blank" rel="noopener noreferrer" data-testid="hero-playstore" className="ld-btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-black">
              <Play className="h-4 w-4" /> Get it on Google Play
            </a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-4 text-xs font-bold uppercase tracking-widest text-[var(--ld-muted)]"
          >
            Private by design · Worldwide Unit System · You own your data "Period"
          </motion.p>

          {/* Quick highlights to fill the column */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.7 }}
            className="mt-8 grid max-w-lg grid-cols-2 gap-3"
          >
            {[
              { icon: Calculator, label: "Granular & liquid NPK", sub: "Exact rates, every time" },
              { icon: CloudSun, label: "7-day weather & soil", sub: "Live for your location" },
              { icon: Flame, label: "GDD 50°F / 32°F", sub: "Warm & cool season" },
              { icon: Package, label: "120+ product library", sub: "Label rates built in" },
              { icon: Globe2, label: "Worldwide unit system", sub: "Imperial & metric" },
              { icon: Sparkles, label: "AI assistant & diagnosis", sub: "Photo-based problem ID" },
            ].map((h, i) => {
              const Icon = h.icon;
              return (
                <div
                  key={i}
                  data-testid={`hero-highlight-${i}`}
                  className="flex items-center gap-3 rounded-xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/50 px-3.5 py-3 backdrop-blur"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--ld-lime)]/12 text-[var(--ld-lime)]">
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <span className="leading-tight">
                    <span className="block text-[13px] font-black text-[var(--ld-text)]">{h.label}</span>
                    <span className="block text-[11px] font-semibold text-[var(--ld-muted)]">{h.sub}</span>
                  </span>
                </div>
              );
            })}
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.55, duration: 0.6 }}
            data-testid="hero-and-more"
            className="ld-display mt-5 max-w-lg text-center text-4xl text-[var(--ld-text)] sm:text-5xl"
          >
            &amp; <span className="text-[var(--ld-lime)]">More</span>
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7, duration: 0.6 }}
            data-testid="hero-zero-ads"
            className="ld-display mt-2 max-w-lg text-center text-2xl text-[var(--ld-text)] sm:text-3xl"
          >
            Zero <span className="text-[var(--ld-lime)]">Ads</span> — Period
          </motion.p>
        </motion.div>

        {/* Right: live instruments (stacked) */}
        <motion.div
          initial={{ opacity: 0, y: 40, rotateX: 8 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ perspective: 1000 }}
          className="flex flex-col gap-5"
        >
          <LiveClimate />
        </motion.div>
      </div>
    </section>
  );
}
